"use strict";
/**
 * Calibration-class detection and RSM parameter extraction.
 *
 * The engine supports two calibration classes:
 *  - RSM  (Andrich Rating-Scale Model): items share a τ vector within a section;
 *         the per-item parameter is item-location δ. SHAW-CAT calibrations are
 *         this class.
 *  - GRM  (Samejima Graded Response Model): per-item discrimination α and
 *         per-item per-category thresholds β. PROMIS-style calibrations.
 *
 * Detection heuristic: a section is RSM if it has ≥ 2 items and the non-null
 * β-tuples (taken in value-order) cluster into ≤ 2 nearly-identical groups
 * (within a small tolerance). Anything else is GRM.
 *
 * The ≤ 2 allowance accommodates the SHAW-CAT MQ/SQ pattern of two slightly
 * different τ vectors for frequency vs intensity items within the same section.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractRSMParams = exports.detectCalibrationClass = void 0;
/**
 * @typedef {import('./types').IRTConfig} IRTConfig
 * @typedef {import('./types').QuestionConfig} QuestionConfig
 * @typedef {import('./types').CalibrationClass} CalibrationClass
 * @typedef {import('./types').RSMParams} RSMParams
 */
/** Tolerance for treating two τ-vectors as effectively identical (logits). */
const TAU_CLUSTER_TOLERANCE = 0.05;
/**
 * Extract the non-null β values from a QuestionConfig in value-ascending order.
 * Returns the τ vector for that item under RSM interpretation.
 *
 * @param {QuestionConfig} cfg
 * @returns {number[]}
 */
function extractTauVector(cfg) {
    // Sort coefficients by numeric value to get a consistent τ ordering.
    const sorted = [...cfg.coefficients].sort((a, b) => Number(a.value) - Number(b.value));
    return sorted
        .map((c) => c.beta)
        .filter(
    /** @returns {b is number} */
    (b) => b !== null && b !== undefined);
}
/**
 * Are two τ-vectors essentially identical within tolerance?
 *
 * @param {number[]} a
 * @param {number[]} b
 * @returns {boolean}
 */
function tauVectorsEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i += 1) {
        if (Math.abs(a[i] - b[i]) > TAU_CLUSTER_TOLERANCE)
            return false;
    }
    return true;
}
/**
 * Cluster a list of τ-vectors. Returns the number of distinct clusters.
 *
 * @param {number[][]} vectors
 * @returns {number}
 */
function distinctTauClusters(vectors) {
    /** @type {number[][]} */
    const clusters = [];
    for (let i = 0; i < vectors.length; i += 1) {
        const v = vectors[i];
        const found = clusters.some((c) => tauVectorsEqual(c, v));
        if (!found)
            clusters.push(v);
    }
    return clusters.length;
}
/** Minimum item count before the ≤2-cluster heuristic is allowed to vote
 *  for RSM. Below this we default to GRM to avoid misclassifying small
 *  PROMIS pilot or short-form banks (2-3 items with distinct β-vectors
 *  would trivially have ≤2 clusters under the original rule). */
const MIN_ITEMS_FOR_RSM_HEURISTIC = 3;
/**
 * Detect whether a calibration is RSM-class or GRM-class.
 *
 * Decision order:
 *  1. Explicit `$meta.calibrationClass` override on the config → used directly.
 *  2. Empty / single-item config → 'GRM' (insufficient evidence for shared τ).
 *  3. Mismatched τ-vector lengths across items → 'GRM' (can't be rating-scale).
 *  4. ≥ `MIN_ITEMS_FOR_RSM_HEURISTIC` items AND ≤ 2 distinct τ clusters → 'RSM'.
 *  5. Otherwise → 'GRM'.
 *
 * @param {IRTConfig & { $meta?: { calibrationClass?: CalibrationClass } }} config
 * @returns {CalibrationClass}
 */
function detectCalibrationClass(config) {
    if (!config || typeof config !== 'object')
        return 'GRM';
    // (1) Explicit override — calibration authoring can pin the class.
    if (config.$meta &&
        (config.$meta.calibrationClass === 'RSM' ||
            config.$meta.calibrationClass === 'GRM')) {
        return config.$meta.calibrationClass;
    }
    // Treat $meta key as non-item so it doesn't appear in the iteration.
    const ids = Object.keys(config).filter((k) => k !== '$meta');
    // (2) Insufficient items for the heuristic.
    if (ids.length < MIN_ITEMS_FOR_RSM_HEURISTIC)
        return 'GRM';
    const tauVectors = ids.map((id) => extractTauVector(config[id]));
    // (3) All τ-vectors must have the same length under any rating-scale model.
    const firstLen = tauVectors[0].length;
    if (firstLen === 0)
        return 'GRM';
    if (!tauVectors.every((v) => v.length === firstLen))
        return 'GRM';
    // (4) Cluster with the small tolerance. RSM allows ≤ 2 effective clusters
    //     (the SHAW-CAT MQ/SQ pattern of frequency vs intensity items).
    const nClusters = distinctTauClusters(tauVectors);
    return nClusters <= 2 ? 'RSM' : 'GRM';
}
exports.detectCalibrationClass = detectCalibrationClass;
/**
 * Extract RSM parameters from an IRTConfig that has been classified as RSM.
 *
 * Returns a mapping questionId → { delta, tau, K, scoreByValue }.
 *  - delta:        the item-location δ_i (from the config's `alpha` field).
 *  - tau:          the item's τ vector — extracted from its own coefficient
 *                  list. Under RSM most items in a section share the same τ,
 *                  but the structure permits the SHAW-CAT MQ/SQ split where a
 *                  section contains two near-identical τ vectors.
 *  - K:            number of thresholds (= response-categories − 1).
 *  - scoreByValue: lookup from patient-response value → IRT-input score.
 *                  Identity for forward-keyed items; reversed for items like
 *                  pq61 where the response scale runs opposite the latent
 *                  trait.
 *
 * @param {IRTConfig} config
 * @returns {Record<string, { delta: number, tau: number[], K: number, scoreByValue: Record<string, number> }>}
 */
function extractRSMParams(config) {
    /** @type {Record<string, { delta: number, tau: number[], K: number, scoreByValue: Record<string, number> }>} */
    const out = {};
    Object.keys(config)
        .filter((k) => k !== '$meta')
        .forEach((id) => {
        const cfg = config[id];
        if (!cfg || typeof cfg !== 'object') {
            throw new Error(`RSM extraction: item '${id}' is missing or malformed`);
        }
        if (!Number.isFinite(cfg.alpha)) {
            throw new Error(`RSM extraction: item '${id}' has missing or non-numeric alpha (item-location δ)`);
        }
        if (!Array.isArray(cfg.coefficients) || cfg.coefficients.length < 2) {
            throw new Error(`RSM extraction: item '${id}' has fewer than 2 coefficients (need at least one threshold)`);
        }
        const tau = extractTauVector(cfg);
        if (tau.length === 0) {
            throw new Error(`RSM extraction: item '${id}' has no non-null β values (no τ thresholds)`);
        }
        /** @type {Record<string, number>} */
        const scoreByValue = {};
        cfg.coefficients.forEach((c) => {
            const score = c.score !== undefined && c.score !== null ? c.score : Number(c.value);
            if (!Number.isFinite(score)) {
                throw new Error(`RSM extraction: item '${id}' has non-numeric score for value '${c.value}'`);
            }
            scoreByValue[c.value] = score;
        });
        out[id] = {
            delta: cfg.alpha,
            tau,
            K: tau.length,
            scoreByValue,
        };
    });
    return out;
}
exports.extractRSMParams = extractRSMParams;
//# sourceMappingURL=detectCalibration.js.map